// Third-party
import React from 'react';
import { deepClone } from '@/utils/objects';

// Utils
import { FormattedHTMLMessage, FormattedMessage, formatMessage } from '@/utils/i18n';
import { SelectInput, SelectOption } from '@/views/components/inputs';

// Images
import { ReactComponent as CsvIcon } from '@/images/csv-icon.svg?react';

// Types and Constants
import { CSV_TEMPLATE_HEADERS, CSV_TAX_TEMPLATE_HEADERS, CSV_HEADER_NORMALIZATION_MAP } from '@/utils/constants';

type Props = {
  file?: File;
  headers?: Array<string>;
  updateHeadersOrder: (updatedHeaders: string[]) => void;
  shouldDisplayTaxAnalysisHeaders: boolean;
};

const DefineHeadersStep: React.FC<Props> = ({ file, headers, updateHeadersOrder, shouldDisplayTaxAnalysisHeaders }) => {
  const CSV_HEADERS = shouldDisplayTaxAnalysisHeaders ? CSV_TAX_TEMPLATE_HEADERS : CSV_TEMPLATE_HEADERS;

  const getOptions = (selected: string) => {
    if (!headers?.length) return [];
    return headers.map(header => ({ label: header, value: header })).filter(i => i.value !== selected);
  };

  const handleSelected = (to: string, from: string) => {
    const list = headers ? deepClone(headers) : [];
    const fromIndex = list.indexOf(from);
    const toIndex = list.indexOf(to);
    const position = list[fromIndex];

    list[fromIndex] = list[toIndex];
    list[toIndex] = position;
    updateHeadersOrder(list);
  };

  return (
    <div className="file-uploader-define-headers-step">
      <FormattedMessage tagName="p" id="Modal.PortfolioUpload.ConfirmHeaders" />
      <div className="file-uploader-define-headers-step-container">
        <div className="file-uploader-define-headers-step-file">
          <CsvIcon className="file-uploader-csv-icon" />
          {file?.name}
        </div>
        <div>
          {headers?.map((header, index) => {
            if (index >= CSV_HEADERS['en-CA'].length) return null;
            const templateHeader = CSV_HEADERS['en-CA'][index];
            // Use the normalization map to convert the template header to camelCase for i18n/backend; fallback to original if not found
            const normalizedKey = CSV_HEADER_NORMALIZATION_MAP[templateHeader] || templateHeader;

            return (
              <div key={index} className="file-uploader-define-headers-step-column">
                <i className="icon-ds-warning" />
                <span className="label">
                  <FormattedHTMLMessage
                    id="Modal.PortfolioUpload.Columns.text"
                    values={{
                      param: formatMessage(`Modal.PortfolioUpload.Columns.${normalizedKey}`),
                    }}
                  />
                </span>

                <SelectInput
                  compact
                  onValueChange={(option: string) => handleSelected(option, header)}
                  selected={headers[index]}
                  placeholder={headers[index]}
                  className="select"
                >
                  {getOptions(headers[index]).map(opt => (
                    <SelectOption key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectOption>
                  ))}
                </SelectInput>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DefineHeadersStep;
